// calendar.js — pure day-agenda aggregation.
// Combines lead follow-ups (reminders), engine tasks, and personal to-dos into
// a single, deduplicated, time-sorted agenda for a given calendar date (WIB).
// No React / Supabase imports — kept pure so it is trivially testable.

const WIB = 'Asia/Jakarta'
const norm = (s) => (s || '').toString().trim().toLowerCase()

// YYYY-MM-DD for a timestamptz/ISO string, in WIB. Returns '' when empty.
export function datePartWIB(iso) {
  if (!iso) return ''
  // Date-only strings ("2026-09-13") are returned as-is to avoid TZ shifting.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  const d = new Date(iso)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-CA', { timeZone: WIB })
}

// HH:mm for a timestamptz/ISO string, in WIB. Returns '' when empty.
export function timePartWIB(iso) {
  if (!iso) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: WIB })
}

const TASK_LABEL = {
  retry_call: 'Retry call', callback: 'Callback', find_pic: 'Cari PIC',
  next_action: 'Next action', meeting: 'Meeting', data_fix: 'Perbaiki data', manual: 'Task',
}

// Normalize each source into a common agenda-item shape:
// { kind, id, date, time, title, sub, agent, done, badge, company, contact }
function fromReminder(r) {
  return {
    kind: 'reminder', id: r.id,
    date: r.follow_up_date || '',
    time: r.follow_up_time || '',
    title: `${r.prospect_name || '(tanpa nama)'}${r.company ? ' · ' + r.company : ''}`,
    sub: r.notes || r.next_action || 'Follow up',
    agent: r.agent || '',
    done: !!r.done,
    badge: 'Follow-up',
    company: r.company || '',
    contact: r.prospect_name || '',
  }
}
function fromTask(t) {
  return {
    kind: 'task', id: t.id,
    date: datePartWIB(t.due_at),
    time: timePartWIB(t.due_at),
    title: `${t.title || TASK_LABEL[t.task_type] || 'Task'}${t.company_name ? ' · ' + t.company_name : ''}`,
    sub: t.description || TASK_LABEL[t.task_type] || t.task_type || '',
    agent: t.owner_agent || '',
    done: t.status === 'completed' || t.status === 'cancelled',
    badge: TASK_LABEL[t.task_type] || t.task_type || 'Task',
    company: t.company_name || '',
    contact: t.contact_name || '',
  }
}
function fromTodo(t) {
  return {
    kind: 'todo', id: t.id,
    date: t.task_date || '',
    time: t.due_time || '',
    title: t.title || '(tanpa judul)',
    sub: t.description || '',
    agent: t.agent || '',
    done: t.status === 'completed',
    badge: 'To-Do',
    company: '',
    contact: '',
  }
}

// Dedupe reminders vs tasks: a reminder created from a call log and an engine
// task can represent the SAME follow-up. When a task and reminder share the
// same agent + company + date, keep the task (richer) and drop the reminder.
function dedupeFollowups(items) {
  const taskKeys = new Set(
    items.filter(i => i.kind === 'task')
      .map(i => `${norm(i.agent)}|${norm(i.company)}|${i.date}`)
      .filter(k => k.split('|')[1]) // only when company present
  )
  return items.filter(i => {
    if (i.kind !== 'reminder') return true
    const k = `${norm(i.agent)}|${norm(i.company)}|${i.date}`
    return !(i.company && taskKeys.has(k))
  })
}

function byTime(a, b) {
  const at = a.time || '99:99'   // untimed items sink to the bottom
  const bt = b.time || '99:99'
  if (at !== bt) return at.localeCompare(bt)
  return (a.title || '').localeCompare(b.title || '')
}

// Build the agenda for a single date.
// opts: { date (YYYY-MM-DD, required), tasks, reminders, todos, agent, includeDone }
// When `agent` is set, only that agent's items are returned (superadmin passes '').
// `includeDone` (default true) keeps completed items visible with a done flag.
export function dayAgenda({ date, tasks = [], reminders = [], todos = [], agent = '', includeDone = true } = {}) {
  if (!date) return []
  const wantAgent = norm(agent)

  let items = [
    ...reminders.map(fromReminder),
    ...tasks.map(fromTask),
    ...todos.map(fromTodo),
  ].filter(i => i.date === date)

  if (wantAgent) items = items.filter(i => norm(i.agent) === wantAgent)
  if (!includeDone) items = items.filter(i => !i.done)

  items = dedupeFollowups(items)
  items.sort(byTime)
  return items
}

// Count agenda items per date within an inclusive [from, to] window — used to
// render dots/badges on a month strip. Returns { 'YYYY-MM-DD': count }.
export function agendaCounts({ tasks = [], reminders = [], todos = [], agent = '', includeDone = false } = {}) {
  const wantAgent = norm(agent)
  const all = [
    ...reminders.map(fromReminder),
    ...tasks.map(fromTask),
    ...todos.map(fromTodo),
  ]
  const counts = {}
  for (const i of all) {
    if (!i.date) continue
    if (wantAgent && norm(i.agent) !== wantAgent) continue
    if (!includeDone && i.done) continue
    counts[i.date] = (counts[i.date] || 0) + 1
  }
  return counts
}
